import {Box, Divider, Grid, Stack, Typography} from '@mui/material';
import {t} from 'i18next';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {DocumentListSourceInfo} from '../../../api/types/document.type';
import {useDocumentList} from '../../../hooks/document/useDocument';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../redux/slice/docListSlice';
import {
  storeDocumentListMasterId,
  storeSourceId,
  storeSourceTypeCode,
} from '../../../redux/slice/docViewSlice';
import {RemoveFromShoppingCart} from '../../../redux/slice/sessionSearchSlice';
import {RootState} from '../../../redux/store';
import {SourceTypeCode, ToastType} from '../../../shared/constants';
import ArrDocMaxLimitWarning from '../../common/arr-doc-max-limit-warning/ArrDocMaxLimitWarning';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {CustomMenuItem} from '../../common/arr-menu/ArrMenu';
import ArrResultTile from '../../common/arr-result-tile/ArrResultTile';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {useDocListActionForDocView} from '../../doc-list/useDocListActionForDocView';
import {
  clearDocViewState,
  createDocumentListRequestPayload,
} from '../../document-viewer/docViewerUtils';
import {IShoppingCartItem, ResultType} from '../types/SessionSearch';
import {SOURCE_TYPE_CODE, TYPE_TO_CONTEXT_MAP} from '../utilities/constants';
import ActionMenu from './action-menu/ActionMenu';
import './ResultShoppingCart.scss';
import {
  useGetDocListMasterIDForExport,
  useReportDocListMasterID,
} from '../../../hooks/reporting/useReporting';
import {setReportDetails} from '../../../redux/slice/comExplorerSlice';
import {useGetSearchUniqueCount} from '../../../hooks/session-search/useSessionSearch';
import type {ISearchUniqueCountRequest} from '../../../api/types/sessionSearch.type';

export interface ShoppingCartProps {
  searchId?: number;
  searchIds?: number[];
  userRoleId: number;
  currentSessionId?: number | string;
  onItemRemoved?: (id: string, type: ResultType) => void;
  onChangeNavigate?: () => void;
}

const RESULTTYPE_SOURCEDETAILS_MAPPING_OBJ: Record<string, string> = {
  [ResultType.EmailThread]: 'emailThread',
  [ResultType.PureHit]: 'pureHits',
  [ResultType.NearDupe]: 'nearDup',
  [ResultType.FamilyMember]: 'family',
  [ResultType.Conceptual]: 'conceptualSimilar',
};

const UNIQUE_COUNT_RESULT_TYPE_MAP: Record<string, string> = {
  [ResultType.PureHit]: 'PureHits',
  [ResultType.EmailThread]: 'threaddoc',
  [ResultType.FamilyMember]: 'family',
  [ResultType.Conceptual]: 'concept',
  [ResultType.NearDupe]: 'nearDup',
};

const toUniqueCountResultType = (type: ResultType): string =>
  UNIQUE_COUNT_RESULT_TYPE_MAP[type] ?? type;

const createSourceDetailsItem = (sourceID: number) => ({
  pureHits: false,
  emailThread: false,
  nearDup: false,
  family: false,
  conceptualSimilar: false,
  sourceID,
});

const createBulkActionContextItem = (sourceID: number) => ({
  ID: sourceID,
  IncludePureHits: false,
  IncludeEmailThread: false,
  IncludeNearDupe: false,
  IncludeFamily: false,
  IncludeConcept: false,
  SourceTypeCode: SOURCE_TYPE_CODE,
});

type SourceDetailsItem = ReturnType<typeof createSourceDetailsItem>;
type SourceDetailsFlag = Exclude<keyof SourceDetailsItem, 'sourceID'>;

const ResultShoppingCart: React.FC<ShoppingCartProps> = ({
  searchId,
  searchIds,
  userRoleId,
  currentSessionId,
  onItemRemoved,
  onChangeNavigate,
}) => {
  const [menuEnabled, setMenuEnabled] = useState<boolean>(false);
  const shoppingCartResults = useSelector(
    (state: RootState) => state.sessionSearch.shoppingCartItems
  );
  const [uniqueDocCount, setUniqueDocCount] = useState<number>(0);
  const [errorToastMessage, setErrorToastMessage] = useState<string>();
  const [showDocLimitDialog, setShowDocLimitDialog] = useState<boolean>(false);
  const [docLimitSourceTypeCode, setDocLimitSourceTypeCode] =
    useState<SourceTypeCode>();
  const selectedMenuItem = useRef<CustomMenuItem | undefined>(undefined);
  const uniqueCountRequestKey = useRef<string>('');
  const {onDocViewAction} = useDocListActionForDocView();
  const {mutate: fetchDocumentList, isPending: isDocListLoading} =
    useDocumentList();
  const {
    mutate: fetchReportDocumentList,
    isPending: isReportDocumentListLoading,
  } = useReportDocListMasterID();

  const {
    mutate: fetchReportDocumentListForExport,
    isPending: isReportDocumentListForExportLoading,
  } = useGetDocListMasterIDForExport();
  const {mutate: fetchSearchUniqueCount} = useGetSearchUniqueCount();

  const getUniqueDocumentCount = (
    shoppingCartItems: IShoppingCartItem[]
  ): number => {
    let uniqueIds = 0;
    shoppingCartItems?.forEach((item) => {
      uniqueIds = uniqueIds + Number(item?.count);
    });
    return uniqueIds;
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedResultCount = () => {
    return shoppingCartResults?.length ?? 0;
  };

  const normalizedSearchIds = useMemo<number[]>(() => {
    const ids = (searchIds?.length ? searchIds : [searchId]).filter(
      (id): id is number => typeof id === 'number' && id > 0
    );
    return Array.from(new Set(ids));
  }, [searchIds, searchId]);

  const getCartItemSourceID = useCallback(
    (item: IShoppingCartItem): number | undefined => {
      const itemSourceID = Number(item.sourceID);
      if (Number.isFinite(itemSourceID) && itemSourceID > 0) {
        return itemSourceID;
      }

      return normalizedSearchIds.length === 1
        ? normalizedSearchIds[0]
        : Number(currentSessionId) || searchId;
    },
    [currentSessionId, normalizedSearchIds, searchId]
  );

  const buildUniqueCountPayload = useCallback((): ISearchUniqueCountRequest[] => {
    const payloadBySourceAndType = new Map<string, ISearchUniqueCountRequest>();

    shoppingCartResults.forEach((item: IShoppingCartItem) => {
      const advSearchID = getCartItemSourceID(item);
      if (!advSearchID || !item.type) return;

      const resultType = toUniqueCountResultType(item.type);
      const key = `${advSearchID}-${resultType}`;
      if (payloadBySourceAndType.has(key)) return;

      payloadBySourceAndType.set(key, {
        advSearchID,
        resultType,
        isSavedSearch: false,
      });
    });

    return Array.from(payloadBySourceAndType.values());
  }, [getCartItemSourceID, shoppingCartResults]);

  const buildSourceDetails = useCallback(() => {
    const sourceDetailsById = new Map<number, SourceDetailsItem>();

    shoppingCartResults.forEach((item: IShoppingCartItem) => {
      const sourceID = getCartItemSourceID(item);
      const resultKey =
        RESULTTYPE_SOURCEDETAILS_MAPPING_OBJ[
          item?.type ?? ResultType.PureHit
        ] as SourceDetailsFlag;

      if (!sourceID || !resultKey) return;

      const sourceDetails =
        sourceDetailsById.get(sourceID) ?? createSourceDetailsItem(sourceID);
      sourceDetails[resultKey] = true;
      sourceDetailsById.set(sourceID, sourceDetails);
    });

    return Array.from(sourceDetailsById.values());
  }, [getCartItemSourceID, shoppingCartResults]);

  const getTooltipText = (resultType: ResultType) => {
    switch (resultType) {
      case ResultType.PureHit:
        return t(
          'sessionSearch.queryResults.standard.results.matchedDocsTooltip'
        );
      case ResultType.EmailThread:
        return t(
          'sessionSearch.queryResults.standard.results.threadedDocsTooltip'
        );
      case ResultType.FamilyMember:
        return t(
          'sessionSearch.queryResults.standard.results.relatedDocsTooltip'
        );
      case ResultType.NearDupe:
        return t(
          'sessionSearch.queryResults.standard.results.nearDupeDocsTooltip'
        );
      case ResultType.Conceptual:
        return t(
          'sessionSearch.queryResults.standard.results.similarDocsTooltip'
        );
    }
  };

  useEffect(() => {
    let cancelled = false;

    setMenuEnabled(shoppingCartResults?.length > 0);
    const fallbackCount = getUniqueDocumentCount(shoppingCartResults);

    if (!shoppingCartResults?.length) {
      uniqueCountRequestKey.current = '';
      setUniqueDocCount(0);
      return;
    }

    const payload = buildUniqueCountPayload();
    if (!payload.length) {
      uniqueCountRequestKey.current = '';
      setUniqueDocCount(fallbackCount);
      return;
    }

    const requestKey = JSON.stringify(payload);
    uniqueCountRequestKey.current = requestKey;
    setUniqueDocCount(fallbackCount);
    fetchSearchUniqueCount(payload, {
      onSuccess: (response) => {
        if (cancelled || uniqueCountRequestKey.current !== requestKey) return;
        setUniqueDocCount(Number(response?.data) || 0);
      },
      onError: () => {
        if (cancelled || uniqueCountRequestKey.current !== requestKey) return;
        setUniqueDocCount(fallbackCount);
      },
    });

    return () => {
      cancelled = true;
    };
  }, [buildUniqueCountPayload, shoppingCartResults]);

  const handleViewInDocView = useCallback(
    (actionId: string, totalDocumentToView: number) => {
      const sourceDetails = buildSourceDetails() as never;
      const objSourceOverrides = {
        sourceTypeCode: SourceTypeCode.SESSIONSEARCH,
        isDefaultLoad: true,
        totalDocumentToView,
      };
      onDocViewAction({
        actionId,
        objSourceOverrides,
        isAllPages: false,
        isChildrens: false,
        sourceDetails,
        docsToExclude: [],
        updatedColumns: [],
        documentListMasterID: 0,
      });
    },
    [buildSourceDetails, onDocViewAction]
  );

  const documentListPayload = useCallback(
    (
      totalDocumentToView: number,
      sourceInfoOverrides: Partial<DocumentListSourceInfo> = {},
      sourceTypeCode: SourceTypeCode = SourceTypeCode.SESSIONSEARCH
    ) => {
      const sourceDetails = buildSourceDetails() as never;
      return createDocumentListRequestPayload({
        sourceId: searchId ?? 0,
        sourceTypeCode,
        docViewSourceTypeCode: SourceTypeCode.SESSIONSEARCH,
        sourceInfoOverrides: {
          sourceDetails,
          totalDocumentToView,
          destinationContextTypeCode: SourceTypeCode.DOCLIST,
          ...sourceInfoOverrides,
        },
      });
    },
    [buildSourceDetails, searchId]
  );

  const handleViewInDocList = useCallback(
    (totalDocumentToView: number) => {
      fetchDocumentList(documentListPayload(totalDocumentToView), {
        onSuccess: (response) => {
          const masterId =
            Number(
              response.docId ??
                response.doclistId ??
                response.documentListID ??
                response.defaultMasterID ??
                response.docViewSessionObjectsRes?.documentListMasterID
            ) || 0;
          clearDocViewState();
          dispatch(
            setDocListBackNavigation({
              label: t('sessionSearch.title'),
              path: `${window.location.pathname}${window.location.search}`,
              searchId: searchId,
            })
          );
          dispatch(setDocListId(masterId));
          dispatch(storeSourceTypeCode(SourceTypeCode.SESSIONSEARCH));
          dispatch(storeSourceId(searchId ?? 0));
          onChangeNavigate?.();
          navigate('/Document/DocList');
        },
        onError: (error) => {
          setErrorToastMessage(error?.message);
        },
      });
    },
    [
      dispatch,
      onChangeNavigate,
      documentListPayload,
      fetchDocumentList,
      navigate,
      searchId,
    ]
  );

  const fetchReportDocListAPICall = useCallback(
    (destinationContextTypeCode: SourceTypeCode, navigateToPath: string) => {
      const sourceTypeCode = SourceTypeCode.SESSIONSEARCH;
      fetchReportDocumentList(
        documentListPayload(
          -1,
          {
            sourceTypeCode,
            destinationContextTypeCode,
          },
          sourceTypeCode
        )?.sourceInfo,
        {
          onSuccess: (response) => {
            if (destinationContextTypeCode === SourceTypeCode.TALLY) {
              navigate('/Report/Tally', {
                state: {
                  navigationSourceType: sourceTypeCode,
                  source: [(response ?? 0).toString()],
                  sourceName: 'DOCLIST',
                },
              });
            } else {
              clearDocViewState();
              dispatch(storeDocumentListMasterId((response ?? 0).toString()));
              dispatch(storeSourceTypeCode(SourceTypeCode.SESSIONSEARCH));
              dispatch(
                setReportDetails({
                  sourceId: (response ?? 0).toString(),
                  sourceName: 'DOCLIST',
                  backSourceLabel: t('sessionSearch.title'),
                  backSourcePath: '/Search/Searches',
                  navigationSourceType: sourceTypeCode,
                  sourceSelectionText: t('sessionSearch.sourceSelectionText'),
                })
              );
              navigate(navigateToPath);
            }
          },
          onError: (error) => {
            setErrorToastMessage(error?.message ?? '');
          },
        }
      );
    },
    [dispatch, documentListPayload, fetchReportDocumentList, navigate]
  );

  const fetchReportDocListForExport = useCallback(
    (destinationContextTypeCode: SourceTypeCode) => {
      const sourceTypeCode = SourceTypeCode.SESSIONSEARCH;
      fetchReportDocumentListForExport(
        documentListPayload(
          -1,
          {
            sourceTypeCode,
            destinationContextTypeCode,
          },
          sourceTypeCode
        )?.sourceInfo,
        {
          onSuccess: (response) => {
            const exportDocListId = String(response ?? '');
            if (!exportDocListId) {
              setErrorToastMessage('Unable to prepare export source.');
              return;
            }
            navigate('/Report/ExportData', {
              state: {
                item: {
                  navigationSourceType: SourceTypeCode.SESSIONSEARCH,
                  source: exportDocListId.toString(),
                  sourceName: SourceTypeCode.DOCUMENT,
                  sourceDisable: true,
                },
              },
            });
          },
          onError: (error) => {
            setErrorToastMessage(error?.message ?? '');
          },
        }
      );
    },
    [dispatch, documentListPayload, fetchReportDocumentListForExport, navigate, normalizedSearchIds]
  );

  const handleActionClick = useCallback(
    (item: CustomMenuItem) => {
      selectedMenuItem.current = {...item};

      if (String(item.id || '').toLowerCase() === 'tally') {
        fetchReportDocListAPICall(SourceTypeCode.TALLY, '/Report/Tally');
        return;
      }

      switch (item.id) {
        case 'view-in-doc-view':
        case 'view-in-doc-view-new-tab':
          setDocLimitSourceTypeCode(SourceTypeCode.DOCVIEW);
          setShowDocLimitDialog(true);
          break;

        case 'view-in-doc-list': {
          setDocLimitSourceTypeCode(SourceTypeCode.DOCLIST);
          setShowDocLimitDialog(true);
          break;
        }

        case 'concept-explorer': {
          fetchReportDocListAPICall(
            SourceTypeCode.CONCEPTEXPLORER,
            '/DataAnalysisReport/ConceptExplorer'
          );
          break;
        }

        case 'export-data': {
          fetchReportDocListForExport(SourceTypeCode.EXPORTDATA);
          break;
        }

        case 'communication-explorer':
          fetchReportDocListAPICall(
            SourceTypeCode.COMMUNICATIONMAP,
            '/DataAnalysisReport/CommunicationExplorerReport'
          );
          break;

        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchReportDocListAPICall, navigate]
  );

  const onContinueDocLimit = useCallback(
    (totalDocumentToView: number) => {
      setShowDocLimitDialog(false);
      switch (docLimitSourceTypeCode) {
        case SourceTypeCode.DOCVIEW: {
          switch (selectedMenuItem.current?.id) {
            case 'view-in-doc-view': {
              handleViewInDocView('ViewInDocView', totalDocumentToView);
              break;
            }
            case 'view-in-doc-view-new-tab': {
              handleViewInDocView('ViewInDocViewInNewTab', totalDocumentToView);
              break;
            }
          }
          break;
        }
        case SourceTypeCode.DOCLIST: {
          handleViewInDocList(totalDocumentToView);
          break;
        }
      }
    },
    [docLimitSourceTypeCode, handleViewInDocView, handleViewInDocList]
  );

  const onCancelDocLimit = useCallback(() => {
    setShowDocLimitDialog(false);
  }, []);

  const bulkActionContext = useMemo(() => {
    if (!shoppingCartResults?.length) return [];
    return buildLstBulkActionContext(shoppingCartResults);
  }, [shoppingCartResults, getCartItemSourceID]);

  function buildLstBulkActionContext(data: IShoppingCartItem[]) {
    const contextById = new Map<
      number,
      ReturnType<typeof createBulkActionContextItem>
    >();

    data.forEach((item) => {
      const sourceID = getCartItemSourceID(item);
      const typeContext = TYPE_TO_CONTEXT_MAP[item.type];

      if (!sourceID || !typeContext) return;

      const context =
        contextById.get(sourceID) ?? createBulkActionContextItem(sourceID);
      Object.entries(typeContext).forEach(([key, value]) => {
        if (value) {
          context[key as keyof typeof context] = value as never;
        }
      });
      contextById.set(sourceID, context);
    });

    return Array.from(contextById.values());
  }

  const renderReultTiles = () => {
    return shoppingCartResults.map((item, index) => (
      <Grid key={`${item.id}-${item.type}`}>
        <ArrResultTile
          value={item.count}
          color={item.color}
          caption={item.caption ?? ''}
          toolTipText={getTooltipText(item.type)}
          dataTestId={`results-threaded-docs-${index}`}
          type="selected"
          onUnSelect={() => {
            onItemRemoved?.(item.id, item.type);
            dispatch(
              RemoveFromShoppingCart({
                id: item.id,
                type: item.type,
                color: item.color,
                count: item.count,
              })
            );
          }}
        />
      </Grid>
    ));
  };

  return (
    <Box className="result-shopping-cart">
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Stack direction="column" tabIndex={0}>
            <Typography
              className="typo-text subhead4_size"
              data-testid={`result-shopping-cart-selected`}
            >
              {' '}
              {t('sessionSearch.selected')}{' '}
            </Typography>
            <Box className="sub-title-box">
              <Typography
                className="typo-text body4_size"
                data-testid={`result-shopping-cart-unique-docs`}
              >
                {uniqueDocCount} {t('sessionSearch.uniqueDocs')}
              </Typography>
            </Box>
          </Stack>
        </Box>
        <ActionMenu
          userRoleId={userRoleId}
          disabled={!menuEnabled}
          onClickActionItem={handleActionClick}
          lstBulkActionContext={bulkActionContext}
          documentCount={uniqueDocCount.toString()}
        />
      </Stack>
      <Divider className="divider" />
      {/* Body */}
      {selectedResultCount() === 0 ? (
        <Box className="emptyBox">
          <Typography
            className="typo-text body5_size"
            data-testid={`result-shopping-cart-empty-shopping-carttext`}
            tabIndex={0}
          >
            {t('sessionSearch.emptyShoppingCarttext')}
          </Typography>
        </Box>
      ) : (
        <Box className="tile-grid-box">
          <Grid container spacing={1}>
            {renderReultTiles()}
          </Grid>
        </Box>
      )}
      <ArrDocMaxLimitWarning
        totalDocumentCount={uniqueDocCount ?? 0}
        show={showDocLimitDialog}
        destination={docLimitSourceTypeCode}
        onContinue={onContinueDocLimit}
        onCancel={onCancelDocLimit}
      />
      <ArrLoadingSpinner
        overlay={true}
        show={
          isDocListLoading ||
          isReportDocumentListLoading ||
          isReportDocumentListForExportLoading
        }
        isOpen={
          isDocListLoading ||
          isReportDocumentListLoading ||
          isReportDocumentListForExportLoading
        }
      />
      {errorToastMessage && errorToastMessage?.length > 0 && (
        <ArrToastNotification
          message={errorToastMessage}
          onClose={() => {
            setErrorToastMessage(undefined);
          }}
          status={[ToastType.Error]}
        />
      )}
    </Box>
  );
};

export default ResultShoppingCart;
